import { ChangeEvent, useEffect, useState } from 'react'
import { useController, UseControllerProps } from 'react-hook-form'

import { ExclamationCircleIcon } from '@heroicons/react/20/solid'

interface InputProps {
  label?: string
  placeholder?: string
  currencies: string[]
  defaultCurrency?: string | null
  showInline?: boolean
  disabled?: boolean
  byDefault?: { price: number; codeCurrency?: string }
  min?: number
  max?: number
  systemPriceId?: number
  className?: string
}

const CurrencyInput = (props: UseControllerProps & InputProps) => {
  const {
    label,
    placeholder,
    currencies,
    showInline,
    disabled,
    byDefault,
    min,
    max,
    defaultCurrency,
    systemPriceId,
    className,
  } = props
  const { field, fieldState } = useController(props)

  const [input, setInput] = useState<{ price: string; codeCurrency: string }>({
    price: '',
    codeCurrency: byDefault?.codeCurrency ?? defaultCurrency ?? currencies[0],
  })

  useEffect(() => {
    if (field.value === undefined) {
      setInput({
        price: '',
        codeCurrency:
          byDefault?.codeCurrency ?? defaultCurrency ?? currencies[0],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value])

  useEffect(() => {
    if (byDefault) {
      field.onChange({
        price: byDefault.price,
        codeCurrency:
          byDefault?.codeCurrency ?? defaultCurrency ?? currencies[0],
        systemPriceId,
      })
      if (input.price === '')
        setInput({
          price: byDefault.price.toString(),
          codeCurrency: byDefault?.codeCurrency ?? currencies[0],
        })
    } else if (defaultCurrency && !byDefault) {
      field.onChange({
        codeCurrency: defaultCurrency,
        systemPriceId,
      })
      setInput({
        price: '',
        codeCurrency: defaultCurrency,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byDefault])

  const onChangeHelper = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const position = value.length - 1
    const code = value.charCodeAt(position)

    if ((code >= 48 && code <= 57) || code === 46 || value === '') {
      if (code === 46 && value.includes('.') && value.indexOf('.') !== position)
        return
      if (max && Number(e.target.value) > max) return
      if (min && Number(e.target.value) < min) return
      setInput({ ...input, price: value })
      field.onChange({
        ...field.value,
        price: Number(e.target.value),
        codeCurrency:
          field?.value?.codeCurrency ?? defaultCurrency ?? currencies[0],
        systemPriceId,
      })
    }
  }

  return (
    <div
      className={`${showInline ? 'inline-flex items-center gap-2' : 'flex-col'} w-full py-1`}
    >
      {label && (
        <label
          className={`${className} block text-sm font-medium text-gray-700`}
          htmlFor={label}
        >
          {label}
        </label>
      )}
      <div className="relative w-full rounded-md shadow-sm">
        <input
          className={`${
            fieldState.error
              ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-400 text-gray-500 focus:border-gray-600 focus:ring-gray-500'
          } block w-full rounded-md pr-10 placeholder:text-slate-400 sm:text-sm`}
          disabled={disabled}
          onChange={onChangeHelper}
          placeholder={placeholder && placeholder}
          type="text"
          value={input.price}
        />
        <div className="absolute right-0 top-0 flex items-center gap-1 pr-0">
          {fieldState.error && (
            <ExclamationCircleIcon
              aria-hidden="true"
              className="h-5 w-5 text-red-500"
            />
          )}

          <select
            className={
              'rounded-md border-transparent bg-transparent py-2 pl-2 pr-7 text-gray-500 ' +
              'focus:border-slate-500 focus:ring-slate-500 sm:text-sm'
            }
            disabled={disabled}
            onChange={(e) => {
              setInput({ ...input, codeCurrency: e.target.value })
              field.onChange({ ...field.value, codeCurrency: e.target.value })
            }}
            value={input.codeCurrency}
          >
            {currencies?.map((item, idx) => (
              <option key={idx} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>
        {fieldState.error && (
          <p className="mt-2 text-sm text-red-600">
            {fieldState.error?.message}
          </p>
        )}
      </div>
    </div>
  )
}

export default CurrencyInput
